import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faXmark, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import "./BillingChartsModal.css";

// chart.js registration is global and idempotent (see BidVsActualTrend); registering the
// line-chart pieces here is additive and never spins up a second chart.js instance.
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

/** The subset of a billing row these charts need. Structurally satisfied by GeneralBilling's
 * BillingItem, so the parent can pass its filtered rows directly. */
export interface BillingChartItem {
  amount: number;
  dateObj: Date;
  date?: string; // display date; "N/A" for undated rows, which are dropped from the monthly charts
  projectNumber: string;
  employee: string;
  hoursQuantity: number;
}

// Undated rows fall back to `new Date()` upstream, which would silently pile into the current
// month. Drop them so the monthly breakdown stays honest.
const isDated = (item: BillingChartItem): boolean => !!item.date && item.date !== "N/A";

interface BillingChartsModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: BillingChartItem[];
}

// One colour per month series, cycled if a range spans more than the palette.
const MONTH_COLORS = [
  "#5b5bf7", "#dc2626", "#0ea5e9", "#22c55e", "#d97706", "#8b5cf6",
  "#ec4899", "#14b8a6", "#f59e0b", "#64748b", "#ef4444", "#3b82f6",
];

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const monthKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const monthLabelFromKey = (key: string): string => {
  const [year, month] = key.split("-");
  return `${MONTH_LABELS[Number(month) - 1] ?? month} ${year}`;
};

const money = (v: number): string =>
  `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

/** Click-and-drag horizontal panning for a scroll container, so a mouse user can grab the chart
 * and slide it instead of relying on a scrollbar or trackpad. Chart hover is suppressed mid-drag
 * via the `.dragging` class so tooltips don't fight the pan. */
function useDragScroll() {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef({ down: false, startX: 0, startLeft: 0 });
  const [dragging, setDragging] = useState(false);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    const el = ref.current;
    if (!el || e.button !== 0) return;
    drag.current = { down: true, startX: e.pageX, startLeft: el.scrollLeft };
    setDragging(true);
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const el = ref.current;
      if (!el || !drag.current.down) return;
      el.scrollLeft = drag.current.startLeft - (e.pageX - drag.current.startX);
    };
    const onUp = () => {
      if (!drag.current.down) return;
      drag.current.down = false;
      setDragging(false);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  return { ref, onMouseDown, dragging };
}

const BillingChartsModal = ({ isOpen, onClose, data }: BillingChartsModalProps): JSX.Element | null => {
  const [projectSearch, setProjectSearch] = useState<string>("");
  const [employeeSearch, setEmployeeSearch] = useState<string>("");
  const projectScroll = useDragScroll();
  const employeeScroll = useDragScroll();

  // Chronologically-sorted set of months present in the data — the series/legend of both charts.
  const months = useMemo(() => {
    const keys = new Set<string>();
    data.forEach((item) => { if (isDated(item)) keys.add(monthKey(item.dateObj)); });
    return Array.from(keys).sort();
  }, [data]);

  // Chart 1: total billing amount per project, one line per month.
  const billingByProject = useMemo(() => {
    const projects = new Set<string>();
    // project -> monthKey -> summed amount
    const totals: Record<string, Record<string, number>> = {};
    data.forEach((item) => {
      if (!isDated(item)) return;
      const project = (item.projectNumber || "").trim();
      if (!project) return;
      projects.add(project);
      const mk = monthKey(item.dateObj);
      totals[project] = totals[project] || {};
      totals[project][mk] = (totals[project][mk] || 0) + (item.amount || 0);
    });

    // Order projects by their all-time billing so the biggest jobs read left-to-right.
    const sorted = Array.from(projects).sort((a, b) => {
      const sum = (p: string) => Object.values(totals[p] || {}).reduce((s, n) => s + n, 0);
      return sum(b) - sum(a);
    });
    const q = projectSearch.trim().toLowerCase();
    const labels = q ? sorted.filter((p) => p.toLowerCase().includes(q)) : sorted;

    const datasets = months.map((mk, idx) => ({
      label: monthLabelFromKey(mk),
      data: labels.map((project) => totals[project]?.[mk] ?? 0),
      borderColor: MONTH_COLORS[idx % MONTH_COLORS.length],
      backgroundColor: `${MONTH_COLORS[idx % MONTH_COLORS.length]}22`,
      borderWidth: 2,
      pointRadius: 2,
      pointHoverRadius: 4,
      tension: 0.3,
      fill: false,
    }));

    return { labels, datasets };
  }, [data, months, projectSearch]);

  // Chart 2: total hours worked per employee, one line per month.
  const hoursByEmployee = useMemo(() => {
    const employees = new Set<string>();
    // employee -> monthKey -> summed hours
    const totals: Record<string, Record<string, number>> = {};
    data.forEach((item) => {
      if (!isDated(item)) return;
      const hours = item.hoursQuantity || 0;
      if (hours <= 0) return;
      const employee = (item.employee || "").trim();
      if (!employee || employee === "N/A" || employee === "Unknown") return;
      employees.add(employee);
      const mk = monthKey(item.dateObj);
      totals[employee] = totals[employee] || {};
      totals[employee][mk] = (totals[employee][mk] || 0) + hours;
    });

    const sorted = Array.from(employees).sort((a, b) => a.localeCompare(b));
    const q = employeeSearch.trim().toLowerCase();
    const labels = q ? sorted.filter((e) => e.toLowerCase().includes(q)) : sorted;

    const datasets = months.map((mk, idx) => ({
      label: monthLabelFromKey(mk),
      data: labels.map((employee) => Number((totals[employee]?.[mk] ?? 0).toFixed(1))),
      borderColor: MONTH_COLORS[idx % MONTH_COLORS.length],
      backgroundColor: `${MONTH_COLORS[idx % MONTH_COLORS.length]}22`,
      borderWidth: 2,
      pointRadius: 2,
      pointHoverRadius: 4,
      tension: 0.3,
      fill: false,
    }));

    return { labels, datasets };
  }, [data, months, employeeSearch]);

  const baseOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index" as const, intersect: false },
    animation: { duration: 250 },
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: { font: { size: 11 }, usePointStyle: true, pointStyleWidth: 10, padding: 14 },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 }, color: "#64748b", maxRotation: 60, minRotation: 45 },
      },
    },
  }), []);

  const billingOptions = useMemo(() => ({
    ...baseOptions,
    plugins: {
      ...baseOptions.plugins,
      tooltip: {
        callbacks: {
          label: (ctx: { parsed: { y: number | null }; dataset: { label?: string } }) =>
            ctx.parsed.y === null ? "" : ` ${ctx.dataset.label}: ${money(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      ...baseOptions.scales,
      y: {
        beginAtZero: true,
        grid: { color: "rgba(226,232,240,0.6)" },
        ticks: { font: { size: 11 }, color: "#64748b", callback: (v: unknown) => money(Number(v)) },
      },
    },
  }), [baseOptions]);

  const hoursOptions = useMemo(() => ({
    ...baseOptions,
    plugins: {
      ...baseOptions.plugins,
      tooltip: {
        callbacks: {
          label: (ctx: { parsed: { y: number | null }; dataset: { label?: string } }) =>
            ctx.parsed.y === null ? "" : ` ${ctx.dataset.label}: ${ctx.parsed.y}h`,
        },
      },
    },
    scales: {
      ...baseOptions.scales,
      y: {
        beginAtZero: true,
        grid: { color: "rgba(226,232,240,0.6)" },
        ticks: { font: { size: 11 }, color: "#64748b", callback: (v: unknown) => `${v}h` },
      },
    },
  }), [baseOptions]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Widen the plot when there are many categories so labels stay legible and the panel scrolls.
  const projectWidth = Math.max(640, billingByProject.labels.length * 46);
  const employeeWidth = Math.max(640, hoursByEmployee.labels.length * 64);

  return (
    <div className="billing-charts-overlay" onClick={onClose}>
      <div
        className="billing-charts-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Billing analytics charts"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="billing-charts-head">
          <div className="billing-charts-title">
            <FontAwesomeIcon icon={faChartLine} />
            <h3>Billing Analytics</h3>
          </div>
          <button className="billing-charts-close" onClick={onClose} aria-label="Close charts">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="billing-charts-body">
          <section className="billing-charts-card">
            <div className="billing-charts-card-head">
              <div className="billing-charts-card-titles">
                <h4>Total Billing per Project / Month</h4>
                <span className="billing-charts-card-sub">Billed amount by project, split by month</span>
              </div>
              <div className="billing-charts-search">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="billing-charts-search-icon" />
                <input
                  type="text"
                  placeholder="Search project..."
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  aria-label="Search projects"
                />
              </div>
            </div>
            {billingByProject.labels.length === 0 ? (
              <div className="billing-charts-empty">
                {projectSearch.trim() ? "No projects match your search." : "No billing data for the current filters."}
              </div>
            ) : (
              <div
                className={`billing-charts-scroll${projectScroll.dragging ? " dragging" : ""}`}
                ref={projectScroll.ref}
                onMouseDown={projectScroll.onMouseDown}
              >
                <div className="billing-charts-canvas" style={{ minWidth: projectWidth }}>
                  <Line data={billingByProject} options={billingOptions} />
                </div>
              </div>
            )}
          </section>

          <section className="billing-charts-card">
            <div className="billing-charts-card-head">
              <div className="billing-charts-card-titles">
                <h4>Employee Hours Worked / Month</h4>
                <span className="billing-charts-card-sub">Total hours logged per employee, split by month</span>
              </div>
              <div className="billing-charts-search">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="billing-charts-search-icon" />
                <input
                  type="text"
                  placeholder="Search employee..."
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  aria-label="Search employees"
                />
              </div>
            </div>
            {hoursByEmployee.labels.length === 0 ? (
              <div className="billing-charts-empty">
                {employeeSearch.trim() ? "No employees match your search." : "No logged hours for the current filters."}
              </div>
            ) : (
              <div
                className={`billing-charts-scroll${employeeScroll.dragging ? " dragging" : ""}`}
                ref={employeeScroll.ref}
                onMouseDown={employeeScroll.onMouseDown}
              >
                <div className="billing-charts-canvas" style={{ minWidth: employeeWidth }}>
                  <Line data={hoursByEmployee} options={hoursOptions} />
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default BillingChartsModal;
